import React, { useState } from 'react';
import { Upload, Copy, Download, RefreshCw } from 'lucide-react';
import { useTheme } from '../theme';
import { logAnalyticsEvent } from '../firebase';

type Direction = 'encode' | 'decode'; // encode: image -> base64, decode: base64 -> image

// Converts an image file to a Base64 data URI and back, entirely in the browser.
export default function ImageBase64Tool({ direction }: { direction: Direction }) {
  const { darkMode, themeClasses } = useTheme();
  const [dataUri, setDataUri] = useState('');
  const [base64Input, setBase64Input] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [fileName, setFileName] = useState('image');

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    setError('');
    setFileName(file.name.replace(/\.[^.]+$/, '') || 'image');
    const reader = new FileReader();
    reader.onload = (ev) => {
      setDataUri(typeof ev.target?.result === 'string' ? ev.target.result : '');
      logAnalyticsEvent('image_encoded', { file_type: file.type, file_size: file.size });
    };
    reader.readAsDataURL(file);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(dataUri);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      /* ignore */
    }
  };

  // Normalize a pasted value into a usable <img> src.
  const previewSrc = (() => {
    const v = base64Input.trim();
    if (!v) return '';
    if (v.startsWith('data:image/')) return v;
    // assume raw base64 of a PNG if no data-URI prefix is given
    return `data:image/png;base64,${v}`;
  })();

  const downloadImage = () => {
    const a = document.createElement('a');
    a.href = previewSrc;
    a.download = `${fileName}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    logAnalyticsEvent('image_decoded', {});
  };

  const clear = () => {
    setDataUri('');
    setBase64Input('');
    setError('');
  };

  if (direction === 'encode') {
    const rawBase64 = dataUri.includes(',') ? dataUri.split(',')[1] : '';
    return (
      <div className={`${themeClasses.cardBg} rounded-3xl border p-5 sm:p-8`}>
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <label className={`cursor-pointer flex items-center gap-2 px-4 py-2 rounded-xl ${themeClasses.buttonSecondary} border transition-all hover:scale-105`}>
            <Upload size={18} />
            Choose image
            <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
          </label>
          {dataUri && (
            <button onClick={clear} className={`flex items-center gap-2 px-4 py-2 rounded-xl ${themeClasses.buttonSecondary} border transition-all hover:scale-105`}>
              <RefreshCw size={16} /> Clear
            </button>
          )}
        </div>

        {error && (
          <div className={`p-4 mb-4 ${themeClasses.errorBg} border rounded-2xl`}>{error}</div>
        )}

        {dataUri && (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className={`text-sm font-semibold mb-2 ${themeClasses.textPrimary}`}>Preview</p>
              <img src={dataUri} alt="Selected preview" className={`max-h-60 rounded-xl border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className={`text-sm font-semibold ${themeClasses.textPrimary}`}>Base64 data URI</p>
                <button
                  onClick={handleCopy}
                  className={`flex items-center gap-1 text-sm ${themeClasses.accentText}`}
                >
                  <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <textarea
                readOnly
                value={dataUri}
                aria-label="Base64 data URI output"
                className={`w-full h-40 p-3 text-xs ${themeClasses.inputBg} border rounded-xl resize-none break-all`}
              />
              <p className={`text-xs mt-2 ${themeClasses.textMuted}`}>
                {rawBase64.length.toLocaleString()} Base64 characters
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // decode: base64 -> image
  return (
    <div className={`${themeClasses.cardBg} rounded-3xl border p-5 sm:p-8`}>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <p className={`text-sm font-semibold mb-2 ${themeClasses.textPrimary}`}>Base64 or data URI</p>
          <textarea
            value={base64Input}
            onChange={(e) => setBase64Input(e.target.value)}
            placeholder="Paste a Base64 image string or data:image/... URI here"
            aria-label="Base64 image input"
            className={`w-full h-48 p-3 text-xs ${themeClasses.inputBg} border rounded-xl resize-none break-all`}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className={`text-sm font-semibold ${themeClasses.textPrimary}`}>Image preview</p>
            {previewSrc && (
              <button
                onClick={downloadImage}
                className={`flex items-center gap-1 text-sm ${darkMode ? 'text-green-400' : 'text-green-600'}`}
              >
                <Download size={14} /> Download
              </button>
            )}
          </div>
          {previewSrc ? (
            <img
              src={previewSrc}
              alt="Decoded result"
              onError={() => setError('That does not look like valid Base64 image data.')}
              className={`max-h-60 rounded-xl border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}
            />
          ) : (
            <div className={`h-48 flex items-center justify-center rounded-xl border ${themeClasses.inputBg} ${themeClasses.textMuted}`}>
              Preview appears here
            </div>
          )}
          {error && <p className={`text-sm mt-2 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>{error}</p>}
        </div>
      </div>
    </div>
  );
}
